import { useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, CircularProgress, IconButton, InputAdornment, Link, Stack, TextField,
  Typography,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import SocialApi from '../api/social';
import BrandLockup from '../components/brand/BrandLockup';
import LoginBackdrop, { PlatformOrbit } from '../components/login/LoginBackdrop';

const LOCKED_MESSAGE = 'Too many failed sign in attempts. Try again in an hour, or ask an administrator to unlock '
  + 'your account.';

/**
 * The sign in page, after the TrialX Social artwork: the logo, a "Social" pill and the tagline on the left, the
 * email and password card on the right among the platforms' tiles (stacked on small screens). onSignedIn gets
 * the new session; App then returns to the page that sent the user here (?next=), else Home.
 */
export default function Login({ session, onSignedIn }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (event) => {
    event.preventDefault();
    if (!username.trim() || !password) {
      setError('Enter your email and password.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      onSignedIn(await SocialApi.login(username.trim(), password));
    } catch (err) {
      setError(err.status === 429 ? LOCKED_MESSAGE : err.message);
      setPassword('');
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ position: 'relative', minHeight: '100vh', display: 'flex', flexDirection: 'column',
      overflow: 'hidden' }}>
      <LoginBackdrop />

      <Box sx={{ position: 'relative', zIndex: 1, flex: 1, width: '100%', maxWidth: 1280, mx: 'auto',
        display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: 'center',
        justifyContent: { xs: 'center', md: 'space-between' }, gap: { xs: 4, md: 6 },
        px: { xs: 2, sm: 4, md: 8 }, py: { xs: 5, md: 8 } }}>
        <Brand />

        <Box sx={{ position: 'relative', width: '100%', maxWidth: 400, mr: { lg: 8 } }}>
          <PlatformOrbit />
          <Card sx={{ position: 'relative', borderRadius: 4, border: 0,
            boxShadow: '0 24px 60px rgba(13, 46, 211, 0.12), 0 2px 6px rgba(37, 45, 82, 0.06)' }}>
            <CardContent component="form" noValidate onSubmit={submit}
                         sx={{ p: { xs: 3, sm: 4 }, '&:last-child': { pb: { xs: 3, sm: 4 } } }}>
              <Typography variant="h5" component="h1" sx={{ fontWeight: 600, mb: 0.5 }}>Sign in</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Use your TrialX account.
              </Typography>

              {error && <Alert severity="error" sx={{ mb: 2.5 }}>{error}</Alert>}

              <Stack spacing={2.5}>
                <TextField label="Email" type="email" value={username} onChange={(e) => setUsername(e.target.value)}
                           autoComplete="username" autoFocus fullWidth disabled={submitting} />
                <TextField label="Password" type={showPassword ? 'text' : 'password'} value={password}
                           onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" fullWidth
                           disabled={submitting}
                           InputProps={{
                             endAdornment: (
                               <InputAdornment position="end">
                                 <IconButton onClick={() => setShowPassword((shown) => !shown)} edge="end"
                                             aria-label={showPassword ? 'Hide password' : 'Show password'}>
                                   {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                 </IconButton>
                               </InputAdornment>
                             ),
                           }} />
                <Button type="submit" variant="contained" size="large" fullWidth disabled={submitting}
                        sx={{ py: 1.25, fontSize: 14 }}
                        startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}>
                  {submitting ? 'Signing in…' : 'Sign in'}
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Box>
      </Box>

      <Footer links={session?.links} />
    </Box>
  );
}

// The logo with its "Social" pill, then the tagline; centred over the card on small screens
function Brand() {
  return (
    <Stack spacing={{ xs: 2, md: 3.5 }} alignItems={{ xs: 'center', md: 'flex-start' }}
           sx={{ textAlign: { xs: 'center', md: 'left' } }}>
      <BrandLockup height={{ xs: 56, md: 64, lg: 76 }} />
      <Typography component="p" sx={{ color: '#3B4566', fontWeight: 400, lineHeight: 1.45,
        fontSize: { xs: 18, md: 28, lg: 32 }, pl: { md: 1 } }}>
        Smarter Insights.
        <br />
        Stronger Campaigns.
      </Typography>
    </Stack>
  );
}

function Footer({ links }) {
  return (
    <Stack component="footer" direction="row" spacing={3} useFlexGap alignItems="center" flexWrap="wrap"
           justifyContent={{ xs: 'center', md: 'flex-start' }}
           sx={{ position: 'relative', zIndex: 1, width: '100%', maxWidth: 1280, mx: 'auto',
             px: { xs: 2, sm: 4, md: 8 }, pb: 3 }}>
      <Typography variant="body2" color="text.secondary">© {new Date().getFullYear()} TrialX</Typography>
      {links && (
        <>
          <Link href={links.terms_of_use} target="_blank" rel="noopener noreferrer" underline="hover"
                sx={{ fontSize: 14, fontWeight: 500 }}>
            Terms of Use
          </Link>
          <Link href={links.privacy_policy} target="_blank" rel="noopener noreferrer" underline="hover"
                sx={{ fontSize: 14, fontWeight: 500 }}>
            Privacy Policy
          </Link>
        </>
      )}
    </Stack>
  );
}
